import type { JSX } from 'react';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { IPredicate, IPredicateGroup, Segment } from '@feathr/blackbox';
import { Button } from '@feathr/components';
import type { IUseTableSelectionReturn } from '@feathr/extender/hooks';

import ConfirmArchivePeopleModal from '../../ConfirmArchivePeopleModal';
import type { TArchiveAction } from '../../types';

interface IArchiveButtonProps {
  action: TArchiveAction;
  predicates?: Array<IPredicate | IPredicateGroup>;
  selection: IUseTableSelectionReturn;
  segment: Segment;
  onSuccess?: () => void;
  onSelectAllArchiveStart?: () => void;
}

function ArchiveButton({
  action,
  predicates,
  segment,
  selection,
  onSuccess,
  onSelectAllArchiveStart,
}: Readonly<IArchiveButtonProps>): JSX.Element {
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const { t } = useTranslation();

  const { isSelectAllMode, count } = selection;
  const isArchiveAction = action === 'archive';

  function handleOpenModal(): void {
    setIsModalOpen(true);
  }

  function handleCloseModal(): void {
    setIsModalOpen(false);
  }

  function getButtonText(): string {
    if (isSelectAllMode) {
      return isArchiveAction ? t('Archive all people') : t('Restore all people');
    }
    return isArchiveAction
      ? t('Archive {{count}} person', { count: count })
      : t('Restore {{count}} person', { count: count });
  }

  return (
    <>
      <Button onClick={handleOpenModal}>{getButtonText()}</Button>

      {isModalOpen && (
        <ConfirmArchivePeopleModal
          action={action}
          onClose={handleCloseModal}
          onSelectAllArchiveStart={onSelectAllArchiveStart}
          onSuccess={onSuccess}
          opened={isModalOpen}
          predicates={predicates}
          segment={segment}
          selection={selection}
        />
      )}
    </>
  );
}

export default ArchiveButton;
